/**
 * 下拉菜单栏（M2-02 / DESIGN.md §7.1）。
 *
 * 行为照桌面应用惯例：
 * - 点组名开/关下拉；菜单已开时移到别的组名上直接切换
 * - Esc 关闭并把焦点还给组名；↑/↓ 在项间移动；←/→ 切组；Enter 执行
 * - 禁用项如实置灰（aria-disabled），点击与 Enter 都不触发
 * - **只有一项的组直接执行那一项**（M5-05 之前的「帮助」组只有「关于」
 *   一项，现在多了「帮助…」，两项了就走正常下拉），单项还开一层下拉是
 *   无谓的一跳
 *
 * 分层：本组件只消费 {@link MenuGroupModel} 与回调，不 import engine/gpu/io。
 */
import type { JSX } from 'solid-js';
import { Index, Show, createSignal, onCleanup, onMount } from 'solid-js';
import type { MenuGroupModel } from './menu-model';
import styles from './MenuBar.module.css';

export interface MenuBarProps {
  readonly model: readonly MenuGroupModel[];
  readonly onExecute: (actionId: string) => void;
}

export function MenuBar(props: MenuBarProps): JSX.Element {
  const [openGroup, setOpenGroup] = createSignal<string | null>(null);
  const [focusIndex, setFocusIndex] = createSignal(-1);
  /** 当前展开的二级菜单（父项 id）。切组/关闭菜单都要连带清掉。 */
  const [openSubmenu, setOpenSubmenu] = createSignal<string | null>(null);

  let rootEl: HTMLDivElement | undefined;

  const close = (): void => {
    setOpenGroup(null);
    setFocusIndex(-1);
    setOpenSubmenu(null);
  };

  const groupModel = (name: string): MenuGroupModel | undefined =>
    props.model.find((g) => g.group === name);

  const execute = (actionId: string, enabled: boolean): void => {
    if (!enabled) return;
    close();
    props.onExecute(actionId);
  };

  const activateGroup = (group: MenuGroupModel): void => {
    // 单项组直接执行，见文件头
    if (group.items.length === 1) {
      close();
      const only = group.items[0]!;
      if (only.enabled) props.onExecute(only.id);
      return;
    }
    if (openGroup() === group.group) close();
    else {
      setOpenGroup(group.group);
      setFocusIndex(0);
    }
  };

  const moveFocus = (delta: number): void => {
    const group = groupModel(openGroup() ?? '');
    if (group === undefined) return;
    const count = group.items.length;
    setFocusIndex(((focusIndex() + delta) % count + count) % count);
  };

  const moveGroup = (delta: number): void => {
    const current = openGroup();
    if (current === null) return;
    // 只在多项组之间循环——单项组是按钮，不该被「打开」
    const groups = props.model.filter((g) => g.items.length > 1);
    const at = groups.findIndex((g) => g.group === current);
    if (at < 0) return;
    const next = groups[(at + delta + groups.length) % groups.length]!;
    setOpenGroup(next.group);
    setFocusIndex(0);
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (openGroup() === null) return;
    switch (event.key) {
      case 'Escape':
        close();
        break;
      case 'ArrowDown':
        moveFocus(1);
        break;
      case 'ArrowUp':
        moveFocus(-1);
        break;
      case 'ArrowRight':
        moveGroup(1);
        break;
      case 'ArrowLeft':
        moveGroup(-1);
        break;
      case 'Enter':
      case ' ': {
        const group = groupModel(openGroup() ?? '');
        const item = group?.items[focusIndex()];
        if (item !== undefined) execute(item.id, item.enabled);
        break;
      }
      default:
        return;
    }
    event.preventDefault();
    event.stopPropagation();
  };

  onMount(() => {
    // 点菜单外的任何地方关闭。pointerdown 而非 click：拖拽开始也该关
    const onPointerDown = (event: PointerEvent): void => {
      if (rootEl !== undefined && event.target instanceof Node && !rootEl.contains(event.target)) {
        close();
      }
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown, true);
    onCleanup(() => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown, true);
    });
  });

  return (
    <div
      ref={(el) => {
        rootEl = el;
      }}
      class={styles.root}
      data-testid="menu-root"
    >
      <Index each={props.model}>
        {(group) => (
          <div class={styles.group}>
            <button
              type="button"
              class={styles.groupButton}
              classList={{ [styles.groupOpen!]: openGroup() === group().group }}
              data-testid={group().group === '帮助' ? 'menu-help' : `menu-group-${group().group}`}
              aria-haspopup={group().items.length > 1 ? 'menu' : undefined}
              aria-expanded={group().items.length > 1 ? openGroup() === group().group : undefined}
              onClick={() => {
                activateGroup(group());
              }}
              onPointerEnter={() => {
                // 菜单已开时划过即切换（桌面惯例）
                if (openGroup() !== null && group().items.length > 1) {
                  setOpenGroup(group().group);
                  setFocusIndex(0);
                  setOpenSubmenu(null);
                }
              }}
            >
              {group().group}
            </button>
            <Show when={openGroup() === group().group && group().items.length > 1}>
              <div class={styles.dropdown} role="menu" data-testid={`menu-dropdown-${group().group}`}>
                <Index each={group().items}>
                  {(item, index) => (
                    <>
                      <Show when={index > 0 && group().items[index - 1]!.section !== item().section}>
                        <div class={styles.separator} role="separator" />
                      </Show>
                      <Show
                        when={item().submenu === undefined}
                        fallback={
                          <div class={styles.submenuWrapper}>
                            <button
                              type="button"
                              role="menuitem"
                              aria-haspopup="menu"
                              aria-expanded={openSubmenu() === item().id}
                              class={styles.item}
                              classList={{ [styles.itemFocused!]: focusIndex() === index }}
                              data-testid={`menu-item-${item().id}`}
                              aria-disabled={!item().enabled}
                              disabled={!item().enabled}
                              onClick={() => {
                                // 不做"再点一次关闭"的切换：鼠标点击的路径必然先经过
                                // onPointerEnter（已经把它打开了），切换语义会让
                                // 点击直接把刚打开的子菜单关掉——移到别的项或
                                // Esc/点外面才应该关，跟顶层组名那种"点开点关"
                                // 不是同一种交互
                                if (item().enabled) setOpenSubmenu(item().id);
                              }}
                              onPointerEnter={() => {
                                setFocusIndex(index);
                                if (item().enabled) setOpenSubmenu(item().id);
                              }}
                            >
                              <span class={styles.itemTitle}>{item().title}</span>
                              <span class={styles.itemShortcut}>▶</span>
                            </button>
                            <Show when={openSubmenu() === item().id}>
                              <div
                                class={styles.dropdown}
                                classList={{ [styles.submenuFlyout!]: true }}
                                role="menu"
                                data-testid={`menu-submenu-${item().id}`}
                              >
                                <Index each={item().submenu!}>
                                  {(sub) => (
                                    <button
                                      type="button"
                                      role="menuitem"
                                      class={styles.item}
                                      data-testid={`menu-item-${sub().id}`}
                                      aria-disabled={!sub().enabled}
                                      disabled={!sub().enabled}
                                      onClick={() => {
                                        execute(sub().id, sub().enabled);
                                      }}
                                    >
                                      <span class={styles.itemTitle}>{sub().title}</span>
                                      <span class={styles.itemShortcut}>{sub().shortcutText}</span>
                                    </button>
                                  )}
                                </Index>
                              </div>
                            </Show>
                          </div>
                        }
                      >
                        <button
                          type="button"
                          role="menuitem"
                          class={styles.item}
                          classList={{ [styles.itemFocused!]: focusIndex() === index }}
                          data-testid={`menu-item-${item().id}`}
                          aria-disabled={!item().enabled}
                          disabled={!item().enabled}
                          onClick={() => {
                            execute(item().id, item().enabled);
                          }}
                          onPointerEnter={() => {
                            setFocusIndex(index);
                            setOpenSubmenu(null);
                          }}
                        >
                          <span class={styles.itemTitle}>{item().title}</span>
                          <span class={styles.itemShortcut}>{item().shortcutText}</span>
                        </button>
                      </Show>
                    </>
                  )}
                </Index>
              </div>
            </Show>
          </div>
        )}
      </Index>
    </div>
  );
}
